import React, {Suspense} from 'react';
import {createBrowserRouter, RouterProvider, RouteObject} from 'react-router-dom';
import {appRoutes} from '../../app.routes';
import {LOG_EVENTS} from '@techsee/techsee-common/lib/constants/event-logs.constants';

import {
    wrapLoaderWithCache,
    wrapLoaderWithContext,
    wrapLoaderWithAuth,
    wrapLoaderSequential,
    wrapLoaderForParentRoute,
    withLazyPageWrapper,
    withStoresProvider,
    withResponsiveComponent,
    registerLazyLoaderPlaceholder,
    TechseeRoute
} from './routing.infra';
import {sendSentryLog} from '../../error-reporting';
import {getRootStore, addErrorEventLog} from '../app.bootstrap';
import {runInAction} from 'mobx';

/**
 * Calculates the full route path from parent and current path
 */
function calculateRoutePath(path: string | undefined, parentPath: string): string {
    if (!path) {
        const base = parentPath || '/';

        return base.endsWith('/') ? base + '__index' : base + '/__index';
    }
    if (path.startsWith('/')) {
        return path;
    }
    const separator = parentPath.endsWith('/') ? '' : '/';
    return parentPath + separator + path;
}

/**
 * Applies all loader wrappers: auth, context, cache, and sequential execution.
 * Wrapping order (inside out): Sequential -> Cache -> Context -> Auth -> Actual Loader
 */
function wrapLoaderComplete(
    loader: any,
    authMethod: any,
    currentPath: string,
    parentPath?: string,
    ignoreReturnUrl?: boolean
): any {
    if (!loader) return loader;

    let wrappedLoader = wrapLoaderWithAuth(loader, authMethod, ignoreReturnUrl);
    wrappedLoader = wrapLoaderWithContext(wrappedLoader);
    wrappedLoader = wrapLoaderWithCache(wrappedLoader, currentPath);
    wrappedLoader = wrapLoaderSequential(wrappedLoader, currentPath, parentPath);

    return wrappedLoader;
}

/**
 * Wraps component with responsive component, lazy page wrapper, and stores provider
 */
function wrapComponentWithHOCs(Component: any, MobileComponent: any, storeAs: string | undefined, loader: any): any {
    let wrappedComponent = withResponsiveComponent(Component, MobileComponent);

    if (storeAs && loader) {
        wrappedComponent = withStoresProvider(withLazyPageWrapper(wrappedComponent) as any, storeAs);
    }

    return wrappedComponent;
}

// Recursively process routes and wrap lazy-loaded components
function processRoute(route: TechseeRoute, parentPath: string = ''): RouteObject {
    const processedRoute: RouteObject = {...(route as any)};

    // Calculate current path
    const currentPath = calculateRoutePath(route.path, parentPath);

    const hasChildren = route.children?.length > 0;

    // For parent routes (routes with children): prevent the loader from re-running when
    // navigating between sibling child routes. Two layers of protection:
    // 1. shouldRevalidate: () => false — blocks post-render navigations (React Router consults this)
    // 2. wrapLoaderForParentRoute — blocks initial-load redirects (shouldRevalidate is not called
    //    during the initial page load, so the cache is the only guard in that scenario)
    if (hasChildren && !route.shouldRevalidate) {
        processedRoute.shouldRevalidate = () => false;
    }

    // Handle direct loaders (non-lazy routes)
    if (route.loader) {
        let wrappedLoader = wrapLoaderComplete(
            route.loader,
            route.authMethod,
            currentPath,
            parentPath || undefined,
            route.ignoreReturnUrl
        );

        if (hasChildren) {
            wrappedLoader = wrapLoaderForParentRoute(wrappedLoader, currentPath);
        }

        processedRoute.loader = wrappedLoader;
    }

    // Handle lazy routes
    if (route.lazy) {
        if (hasChildren) {
            registerLazyLoaderPlaceholder(currentPath);
        }

        processedRoute.lazy = async () => {
            let module;

            try {
                module = await route.lazy!();
            } catch (chunkError) {
                addErrorEventLog(LOG_EVENTS.routeChunkLoadError.type, {
                    routePath: currentPath,
                    error: chunkError instanceof Error ? chunkError.message : String(chunkError),
                    stack: chunkError instanceof Error ? chunkError.stack : undefined
                });

                sendSentryLog(chunkError);
                throw chunkError;
            }

            const moduleData = (module as any).default || module;
            const {loader, Component, MobileComponent, storeAs, errorElement} = moduleData;

            const wrappedComponent = wrapComponentWithHOCs(Component, MobileComponent, storeAs, loader);

            // Wrap loader with all protections
            let wrappedLoader = wrapLoaderComplete(
                loader,
                route.authMethod,
                currentPath,
                parentPath || undefined,
                route.ignoreReturnUrl
            );

            if (hasChildren) {
                wrappedLoader = wrapLoaderForParentRoute(wrappedLoader, currentPath);
            }

            return {
                Component: wrappedComponent,
                loader: wrappedLoader,
                errorElement
            };
        };
    }

    // Process children recursively
    if (hasChildren) {
        processedRoute.children = route.children.map((child: any) => processRoute(child, currentPath));
    }

    return processedRoute;
}

// Create router with routes processed by processRoute
function createRouter() {
    const processedRoutes = appRoutes.map((route) => processRoute(route as any));
    return createBrowserRouter(processedRoutes);
}

// Router Provider Component
export const RouterProviderComponent: React.FC = () => {
    const rootStore = getRootStore();

    const showGlobalLoader = () => {
        queueMicrotask(() => runInAction(() => (rootStore.appState.showGlobalLoader = true)));
        return null;
    };

    const router = React.useMemo(() => createRouter(), []);

    return (
        <Suspense fallback={showGlobalLoader() || null}>
            <RouterProvider router={router} fallbackElement={showGlobalLoader() || null} />
        </Suspense>
    );
};
